import { useContext, useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

import {
  toast,
  Modal,
  ModalContext,
  ViewerContext,
  UpgradeSubscriptionModalType,
  Button,
  useCheckIfSubscriptionFeatureActiveBroker,
} from "dash-ui";
import { RecurringTypesEnum } from "dash-common";

import { usePaymentConfirmation } from "common/hooks/use-payment-confirmation/use-payment-confirmation";
import { readLastAttribution } from "common/hooks/useAttributionCapture/useAttributionCapture";
import {
  SubscriptionProduct,
  useCreateCheckoutSessionMutation,
  useGetSubscriptionProductsQuery,
} from "gql/generated.graphql";

import { IUpgradeSubscriptionModalProps } from "./UpgradeSubscriptionModalV3.interfaces";
import { filterProductsByRecurringType } from "../SubscriptionComponents.helpers";
import {
  buildCheckoutSuccessUrl,
  getProductNameSlug,
  hasLivePaidPlan,
  isHigherPricePerMonth,
  isLowerPricePerMonth,
} from "common/helpers";
import {
  filterSubscriptions,
  getModalContent,
} from "./UpgradeSubscriptionModalV3.helpers";
import { SubscriptionChangeConfirmationModalV2 } from "pages/ProfilePage/components/ProfileBilling/components";
import { useNavigate } from "react-router-dom";
import { RoutesPath } from "flow-components/routes/routes";
import { ENABLE_YEARLY_PLANS } from "../SubscriptionComponents.constants";
import { SubscriptionPlansSection } from "../SubscriptionPlansSection";
import { AnalyticsEvent, trackEvent } from "shared/analytics";

const UpgradeSubscriptionModalV3 = ({
  type = UpgradeSubscriptionModalType.GENERAL,
  open,
  onClose,
  id,
}: IUpgradeSubscriptionModalProps) => {
  // Hooks
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation("pages");
  const { isOpenCurrent, toggleModal, payload, setPayload } =
    useContext(ModalContext);
  const { subscription, broker, account } = useContext(ViewerContext);
  const { isSubscriptionActive } = useCheckIfSubscriptionFeatureActiveBroker();
  const isSeeker = account?.isSeeker;

  const currentUserProduct = subscription?.subscriptionProduct;

  const [_paymentConfirmation] = usePaymentConfirmation({
    onSuccess: () => {
      closeModal();
      toast.success(t("pages:profilePage.billing.successfulUpgradeMessage"));
      if (payload?.onCompleted) {
        payload.onCompleted();
      }
    },
  });

  const defaultRecurringType = RecurringTypesEnum.MONTH;

  const [recurringType, setRecurringType] = useState(defaultRecurringType);
  const [showAllPlans, setShowAllPlans] = useState(false);

  // Derived States
  const isOpen = isOpenCurrent["UpgradeSubscriptionModal"];

  // Sync recurring type when modal opens (to handle cases where viewer data loads after mount)
  useEffect(() => {
    if (isOpen || open) {
      setRecurringType(defaultRecurringType);
    }
  }, [isOpen, open, defaultRecurringType]);

  // Reset showAllPlans when modal closes
  useEffect(() => {
    if (!isOpen && !open) {
      setShowAllPlans(false);
    }
  }, [isOpen, open]);

  const closeModal = () => {
    if (onClose) {
      return onClose();
    }
    toggleModal(false, "UpgradeSubscriptionModal");
  };

  // Queries and mutations
  const { data, loading } = useGetSubscriptionProductsQuery({
    skip: !isOpen && !open,
  });

  // Filter products based on ENABLE_YEARLY_PLANS feature flag
  const subscriptions = data?.getSubscriptionProducts
    ? filterProductsByRecurringType(
        data.getSubscriptionProducts as SubscriptionProduct[]
      )
    : [];

  const [createCheckoutSession, { loading: checkoutLoading }] =
    useCreateCheckoutSessionMutation();

  // Event handlers
  const handleToggleRecurringType = (newRecurringType: RecurringTypesEnum) => {
    setRecurringType(newRecurringType);
  };

  const { title, subtitle } = useMemo(
    () => getModalContent(type, t, theme, isSeeker),
    [type, t, theme, isSeeker]
  );

  // Calculate filtered products to determine actual display count
  const filteredProducts = filterSubscriptions({
    subscriptions,
    isSeeker,
    recurringType,
    modalType: type,
    currentProductId: isSubscriptionActive
      ? subscription?.subscriptionProductId
      : undefined,
    showAllPlans,
    currentUserProduct: isSubscriptionActive ? currentUserProduct : undefined,
  });

  // Determine if "See all plans" button should be shown
  // Only show for modal types that display a filtered subset of plans
  // AND when we're not already showing all available plans (e.g., when current plan is Basic trial)
  const allPlansCount = isSeeker ? 1 : 3;
  const shouldShowSeeAllButton =
    !loading &&
    !showAllPlans &&
    type &&
    (type === UpgradeSubscriptionModalType.PORTFOLIO ||
      type === UpgradeSubscriptionModalType.DOWNLOAD ||
      type === UpgradeSubscriptionModalType.IDX) &&
    filteredProducts.length < allPlansCount;

  async function handleSelectPlan(
    product: SubscriptionProduct,
    withTrial?: boolean
  ) {
    // If there's no broker or no license information (and user is not a seeker), redirect to complete account page
    if (!broker || (!broker.license && !isSeeker)) {
      return navigate(RoutesPath.CompleteSignUpSSO);
    }

    // Create a new subscription (basic -> paid, or a first trial). A live paid
    // plan changes in place instead: Checkout would start a second subscription
    // and keep billing the old one during the trial.
    if (
      !currentUserProduct ||
      currentUserProduct?.price === 0 ||
      (withTrial && !hasLivePaidPlan(subscription))
    ) {
      const productSlug = getProductNameSlug(product);

      trackEvent(AnalyticsEvent.CHECKOUT_STARTED, {
        product_slug: productSlug,
        recurring_type: product.recurringType,
        source: "upgrade_modal",
        gate_type: type,
        coupon_present: false,
      });

      const data = await createCheckoutSession({
        variables: {
          input: {
            priceId: product.stripePriceId,
            productId: product.stripeProductId,
            successUrl: buildCheckoutSuccessUrl(productSlug),
            cancelUrl: window.location.href,
            attribution: readLastAttribution(),
          },
        },
      });

      const sessionUrl = data.data?.createCheckoutSession.sessionUrl;

      if (!sessionUrl) {
        return;
      }

      window.open(sessionUrl, "_blank");
      return closeModal();
    }

    const isCurrent = product.name === currentUserProduct?.name;

    // Update an existing subscription
    let mode: "downgrade" | "upgrade" = "upgrade";
    if (isHigherPricePerMonth(product, currentUserProduct)) {
      mode = isCurrent ? "downgrade" : "upgrade";
    } else if (isLowerPricePerMonth(product, currentUserProduct)) {
      mode = isCurrent ? "upgrade" : "downgrade";
    }

    setPayload({
      newProduct: product,
      mode,
      withTrial,
    });
    toggleModal(true, "SubscriptionChangeConfirmationModalV2");
  }

  return (
    <Modal
      id={id}
      closeIcon
      isOpen={open || isOpen}
      onRequestClose={closeModal}
      title={title}
      viewHierarchy={-2}
      maxWidth={"90%"}
      w="fit-content"
      isCentered={true}
    >
      <div className="text-wrap max-w-3xl xl:max-w-[1100px]">{subtitle}</div>
      <SubscriptionPlansSection
        products={subscriptions}
        loading={loading}
        currentProductId={
          isSubscriptionActive ? subscription?.subscriptionProductId : undefined
        }
        currentUserProduct={
          isSubscriptionActive ? currentUserProduct : undefined
        }
        isSeeker={isSeeker}
        recurringType={recurringType}
        onRecurringTypeChange={handleToggleRecurringType}
        onSelectPlan={handleSelectPlan}
        checkoutLoading={checkoutLoading}
        filterMode="modal"
        modalType={type}
        showAllPlans={showAllPlans}
        modalId={id}
        isSubscriptionActive={isSubscriptionActive ?? undefined}
      />
      {/* See all plans button - hide if no subscriptions available */}
      {shouldShowSeeAllButton && !loading && subscriptions.length > 0 && (
        <div className="mt-6 flex justify-center">
          <Button
            variation="outline"
            onClick={() => setShowAllPlans(true)}
            px={12}
          >
            {t("subscriptionPlans.seeAllPlans")}
          </Button>
        </div>
      )}
      <SubscriptionChangeConfirmationModalV2 />
    </Modal>
  );
};

export default UpgradeSubscriptionModalV3;
